/**
 * Export pages as JPEG images.
 *
 * Rendered in the browser by pdf.js -- the same renderer the editor shows --
 * so the images match what is on screen, and the server (shared with the
 * City's other apps) does no rendering work. One page comes back as a .jpg;
 * several as a .zip of them.
 */

import type { PDFDocumentProxy } from 'pdfjs-dist'
import { zipFiles } from './zip'

/** Pixels per image above which the resolution is lowered, so a plan-sized
 * page still exports (browsers refuse very large canvases). */
const MAX_PIXELS = 36_000_000

export interface ExportResult {
  blob: Blob
  name: string
  pages: number
  /** Pages exported below the chosen resolution because they are so large. */
  reduced: number[]
}

/** Page numbers (1-based, as typed: "1-3, 5, 8-") to 0-based indices. */
export function parsePages(spec: string, count: number): number[] | null {
  const out = new Set<number>()
  for (const part of spec.split(',').map((p) => p.trim()).filter(Boolean)) {
    const match = /^(\d+)?\s*(-)?\s*(\d+)?$/.exec(part)
    if (!match || (!match[1] && !match[3])) return null
    const first = match[1] ? Number(match[1]) : 1
    const last = match[2] ? (match[3] ? Number(match[3]) : count) : first
    if (first < 1 || last > count || first > last) return null
    for (let n = first; n <= last; n++) out.add(n - 1)
  }
  return out.size ? [...out].sort((a, b) => a - b) : null
}

async function renderPage(doc: PDFDocumentProxy, index: number, dpi: number,
                          quality: number): Promise<{ blob: Blob; reduced: boolean }> {
  const page = await doc.getPage(index + 1)
  let scale = dpi / 72
  const natural = page.getViewport({ scale })
  let reduced = false
  if (natural.width * natural.height > MAX_PIXELS) {
    scale *= Math.sqrt(MAX_PIXELS / (natural.width * natural.height))
    reduced = true
  }
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(viewport.width)
  canvas.height = Math.floor(viewport.height)
  const context = canvas.getContext('2d')
  if (!context) throw new Error('This browser cannot draw the page.')
  // JPEG has no transparency: paper is white.
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  // Rendered as for printing: the display intent paces itself by animation
  // frames, which stop while the tab is in the background, so a long export
  // stalled when the user switched away. Comments and form answers print.
  await page.render({ canvasContext: context, viewport, intent: 'print' }).promise
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
  canvas.width = 0                                   // free the pixels now
  canvas.height = 0
  page.cleanup()
  if (!blob) throw new Error(`Page ${index + 1} could not be converted to JPEG.`)
  return { blob, reduced }
}

export async function exportJpeg(
  doc: PDFDocumentProxy,
  pages: number[],
  options: { dpi: number; quality: number; stem: string },
  onProgress?: (done: number, total: number) => void,
): Promise<ExportResult> {
  const files: { name: string; data: Blob }[] = []
  const reduced: number[] = []
  const digits = String(doc.numPages).length
  for (const [k, index] of pages.entries()) {
    onProgress?.(k, pages.length)
    const image = await renderPage(doc, index, options.dpi, options.quality)
    if (image.reduced) reduced.push(index)
    files.push({ name: `${options.stem}_page${String(index + 1).padStart(digits, '0')}.jpg`, data: image.blob })
  }
  onProgress?.(pages.length, pages.length)
  if (files.length === 1) {
    return { blob: files[0].data, name: files[0].name, pages: 1, reduced }
  }
  return { blob: await zipFiles(files), name: `${options.stem}_pages.zip`, pages: files.length, reduced }
}
